import React, { useEffect, useState } from 'react'
import productService from '../services/productService';
import { useCart } from '../contexts/CartContext';
import UnitSelector from './common/UnitSelector';
import ProductSEO from './common/ProductSEO';
import InfoCard, { InfoCardItem, InfoCardBadge } from './common/InfoCard';
import ProductImageGallery from './common/ProductImageGallery';
import LoadingSpinner from './common/LoadingSpinner';
import { normalizeQuantityForBackend } from '../utils/unitHelpers';
import './ProductDetailsOne.css';

const ProductDetailsOne = ({ productId }) => {
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Miktar ve birim kontrolü
    const [quantity, setQuantity] = useState(1);
    const [unit, setUnit] = useState('kg'); // 'piece', 'gram', 'kg'
    const [message, setMessage] = useState('');
    const [isError, setIsError] = useState(false);
    const [showQuantitySheet, setShowQuantitySheet] = useState(false);
    const { addToCart } = useCart();

    // Tab state yönetimi
    const [activeTab, setActiveTab] = useState('description');

    // WhatsApp sipariş mesajı için dinamik URL
    const whatsappMessage = product ? `Merhaba, ${product.name} adlı üründen ${quantity} ${unit === 'kg' ? 'kg' : unit === 'gram' ? 'gr' : 'adet'} sipariş vermek istiyorum.` : '';
    const whatsappUrl = whatsappMessage
        ? `https://wa.me/905394838998?text=${encodeURIComponent(whatsappMessage)}`
        : 'https://wa.me/905394838998';

    // Görsel URL'lerini birleştirmek için API base URL'i (sonunda /api olmadan)
    const imageApiUrl = (process.env.REACT_APP_IMAGE_API_URL || process.env.REACT_APP_API_URL || '').replace(/\/api\/?$/,'');

    useEffect(() => {
        const fetchProductDetails = async () => {
            if (!productId) {
                setError('Ürün ID bulunamadı');
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                const response = await productService.getProductById(productId);
                
                if (response && response.succeeded) {
                    setProduct(response.product);
                    // Ürün yüklendiğinde başlangıç miktarını ayarla
                    if (response.product.saleType === 1) {
                        setQuantity(1); // Her zaman 1kg ile başla
                        setUnit('kg');
                    } else {
                        setQuantity(1);
                        setUnit('piece');
                    }
                } else {
                    console.error('API yanıtı beklenen formatta değil:', response);
                    setError('Ürün detayları yüklenirken bir hata oluştu. API yanıtı geçersiz.');
                }
            } catch (err) {
                console.error('Ürün detayları yüklenemedi:', err);
                setError('Ürün detayları yüklenirken bir hata oluştu. Lütfen daha sonra tekrar deneyin.');
            } finally {
                setLoading(false);
            }
        };

        fetchProductDetails();
    }, [productId]);

    // Yükleniyor durumu
    if (loading) {
        return (
            <section className="product-details py-80">
                <div className="container container-lg">
                    <LoadingSpinner 
                        size="lg" 
                        color="primary" 
                        text="Ürün bilgileri yükleniyor..."
                    />
                </div>
            </section>
        );
    }

    // Hata durumu
    if (error) {
        return (
            <section className="product-details py-80">
                <div className="container container-lg">
                    <div className="alert alert-danger" role="alert">
                        {error}
                    </div>
                </div>
            </section>
        );
    }

    // Ürün bulunamadı durumu
    if (!product) {
        return (
            <section className="product-details py-80">
                <div className="container container-lg">
                    <div className="alert alert-warning" role="alert">
                        Ürün bulunamadı.
                    </div>
                </div>
            </section>
        );
    }

    const handleQuantityChange = (newQuantity, newUnit) => {
        setQuantity(newQuantity);
        setUnit(newUnit);
    };

    // Tab değiştirme fonksiyonu
    const handleTabChange = (tabName) => {
        setActiveTab(tabName);
    };

    const handleAddToCart = async () => {
        if (!product || !product.id) {
            setMessage('Ürün bilgisi bulunamadı.');
            setIsError(true);
            return;
        }

        // Backend için miktarı normalize et (gram cinsinden)
        const normalizedQuantity = normalizeQuantityForBackend(
            quantity, 
            unit, 
            product.saleType || 0
        );
        
        // weightGrams parametresi ile sepete ekle
        const result = await addToCart(
            product.id, 
            1, // quantity (adet olarak 1)
            "", // note
            product.saleType === 1 ? normalizedQuantity : null // weightGrams sadece gram bazlı satışta
        );
        
        if (result.success) {
            setMessage(`Ürün sepete eklendi (${quantity} ${unit === 'kg' ? 'Kg' : unit === 'gram' ? 'Gram' : 'Adet'}).`);
            setIsError(false);
            // Mesajı 3 saniye sonra temizle
            setTimeout(() => {
                setMessage('');
            }, 3000);
        } else {
            setMessage(result.message || 'Ürün sepete eklenirken bir hata oluştu.');
            setIsError(true);
        }
    };

    return (
        <>
            {/* SEO Bileşeni */}
            <ProductSEO product={product} />
            
            <section className="product-details py-80">
                <div className="container container-lg">
                    {/* Hero Section - Görsel ve Temel Bilgiler */}
                    <div className="row gy-4 mb-60">
                        {/* Sol - Görsel Galeri */}
                        <div className="col-lg-6">
                            <ProductImageGallery 
                                mainImage={product.imageUrl}
                                additionalImages={product.additionalImages}
                                productName={product.name}
                                imageApiUrl={imageApiUrl}
                            />
                            
                            {/* Video var ise göster */}
                            {product.videoUrl && (
                                <div className="mt-24">
                                    <div className="video-container rounded-16 overflow-hidden" style={{ boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)' }}>
                                        {product.videoUrl.includes('youtube.com') || product.videoUrl.includes('youtu.be') ? (
                                            <iframe
                                                width="100%"
                                                height="300"
                                                src={product.videoUrl.replace('watch?v=', 'embed/')}
                                                title={`${product.name} Tanıtım Videosu`}
                                                frameBorder="0"
                                                allowFullScreen
                                                style={{ display: 'block' }}
                                            ></iframe>
                                        ) : (
                                            <video
                                                width="100%"
                                                height="300"
                                                controls
                                                className="rounded-16"
                                                style={{ display: 'block' }}
                                            >
                                                <source src={product.videoUrl} type="video/mp4" />
                                                Tarayıcınız video desteklemiyor.
                                            </video>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                        
                        {/* Sağ - Temel Bilgiler Kartı */}
                        <div className="col-lg-6">
                            <div className="product-info-card bg-white rounded-20 shadow-sm p-32 h-100">
                                {/* Badges */}
                                <div className="d-flex flex-wrap gap-8 mb-16">
                                    {product.isNew && (
                                        <span className="badge bg-primary text-white px-12 py-6 rounded-8 fw-semibold">
                                            <i className="ph ph-sparkle me-4"></i>
                                            YENİ ÜRÜN
                                        </span>
                                    )}
                                    {product.isFeatured && (
                                        <span className="badge bg-warning text-white px-12 py-6 rounded-8 fw-semibold">
                                            <i className="ph ph-star me-4"></i>
                                            ÖNE ÇIKAN
                                        </span>
                                    )}
                                    {product.isDiscounted && (
                                        <span className="badge bg-danger text-white px-12 py-6 rounded-8 fw-semibold">
                                            <i className="ph ph-percent me-4"></i>
                                            İNDİRİMLİ
                                        </span>
                                    )}
                                    {product.freeShipping && (
                                        <span className="badge bg-success text-white px-12 py-6 rounded-8 fw-semibold">
                                            <i className="ph ph-truck me-4"></i>
                                            ÜCRETSİZ KARGO
                                        </span>
                                    )}
                                </div>

                                {/* Başlık */}
                                <h3 className="mb-16 fw-bold text-gray-900">{product.name}</h3>
                                
                                {/* Rating ve Review Bilgileri */}
                                <div className="flex-align flex-wrap gap-16 mb-16 pb-16 border-bottom border-gray-100">
                                    {product.rating > 0 && (
                                        <div className="flex-align gap-8">
                                            <div className="flex-align gap-4">
                                                {[...Array(5)].map((_, index) => (
                                                    <i 
                                                        key={index}
                                                        className={`ph-fill ph-star ${index < Math.floor(product.rating) ? 'text-warning' : 'text-gray-300'}`}
                                                    />
                                                ))}
                                            </div>
                                            <span className="text-gray-600 text-sm fw-medium">
                                                {product.rating.toFixed(1)}
                                            </span>
                                            {product.reviewCount > 0 && (
                                                <span className="text-gray-500 text-sm">
                                                    ({product.reviewCount} değerlendirme)
                                                </span>
                                            )}
                                        </div>
                                    )}
                                    
                                    {product.orderCount > 0 && (
                                        <div className="flex-align gap-6">
                                            <i className="ph ph-shopping-bag text-main-600"></i>
                                            <span className="text-gray-600 text-sm fw-medium">
                                                {product.orderCount} kez sipariş edildi
                                            </span>
                                        </div>
                                    )}
                                </div>

                                {/* Kısa Açıklama */}
                                {product.shortDescription && (
                                    <p className="text-gray-700 mb-20">
                                        {product.shortDescription}
                                    </p>
                                )}

                                {/* Barkod */}
                                {product.barcode && (
                                    <div className="mb-16">
                                        <span className="text-gray-500 text-sm">Ürün Kodu: </span>
                                        <span className="text-gray-900 fw-medium">{product.barcode}</span>
                                    </div>
                                )}

                                {/* Fiyat */}
                                <div className="mt-24 mb-24 p-20 bg-light rounded-12">
                                    <div className="d-flex align-items-center gap-12 flex-wrap">
                                        <h2 className="mb-0 text-main-600 fw-bold">
                                            ₺{(product.discountedPrice || product.price).toFixed(2)}
                                        </h2>
                                        {product.discountedPrice && (
                                            <>
                                                <span className="text-xl text-gray-400 text-decoration-line-through">
                                                    ₺{product.price.toFixed(2)}
                                                </span>
                                                <span className="badge bg-danger text-white px-12 py-8 rounded-8">
                                                    %{Math.round((1 - product.discountedPrice / product.price) * 100)} İNDİRİM
                                                </span>
                                            </>
                                        )}
                                    </div>
                                    <span className="text-sm text-gray-500 mt-8 d-block">
                                        / {product.saleType === 1 ? 'Kg' : 'Adet'}
                                    </span>
                                </div>


                                {/* Mesaj */}
                                {message && (
                                    <div className={`alert ${isError ? 'alert-danger' : 'alert-success'} mb-20`} role="alert">
                                        {message}
                                    </div>
                                )}

                                {/* Miktar Seçici */}
                                <div className="product-quantity mb-24">
                                    <UnitSelector
                                        saleType={product.saleType || 0}
                                        minimumOrderWeight={product.minimumOrderWeight || 250}
                                        price={product.discountedPrice || product.price}
                                        discountedPrice={product.discountedPrice}
                                        onQuantityChange={handleQuantityChange}
                                        initialQuantity={product.saleType === 1 ? 1 : 1}
                                        initialUnit={product.saleType === 1 ? 'kg' : 'piece'}
                                        showPrice={true}
                                        size="md"
                                    />
                                </div>

                                {/* Aksiyonlar - Desktop */}
                                <div className="desktop-actions d-none d-md-flex flex-column flex-md-row gap-12">
                                    <button
                                        type="button"
                                        className="btn bg-main-600 text-white hover-bg-main-700 py-20 px-40 rounded-12 d-flex align-items-center justify-content-center gap-12 fw-semibold transition-2 flex-fill"
                                        onClick={handleAddToCart}
                                        disabled={product.isOutOfStock}
                                        style={{ boxShadow: 'none', fontSize: '16px' }}
                                    >
                                        <i className="ph ph-shopping-cart text-xl"></i>
                                        {product.isOutOfStock ? 'Stokta Yok' : 'Sepete Ekle'}
                                    </button>
                                    
                                    <a
                                        href={whatsappUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="btn btn-whatsapp py-20 px-20 rounded-12 d-flex align-items-center justify-content-center transition-2"
                                        style={{ minWidth: '60px' }}
                                        title="WhatsApp ile Sipariş Ver"
                                    >
                                        <i className="ph ph-whatsapp-logo text-2xl"></i>
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Bilgi Kartları Grid */}
                    <div className="product-details-grid mb-60">
                        <div className="row g-4">
                            {/* Ürün Özellikleri Kartı */}
                            <div className="col-md-6 col-lg-3">
                                <InfoCard icon="package" title="Ürün Bilgileri" iconColor="warning">
                                    {product.packageType && (
                                        <InfoCardItem label="Paket Tipi" value={product.packageType} icon="package" />
                                    )}
                                    {product.origin && (
                                        <InfoCardItem label="Menşei" value={product.origin} icon="map-pin" />
                                    )}
                                    {product.shelfLife && (
                                        <InfoCardItem label="Raf Ömrü" value={product.shelfLife} icon="calendar" />
                                    )}
                                    {product.storageConditions && (
                                        <InfoCardItem label="Saklama" value={product.storageConditions} icon="thermometer" />
                                    )}
                                    {product.sku && (
                                        <InfoCardItem label="SKU" value={product.sku} icon="barcode" />
                                    )}
                                    {product.barcode && (
                                        <InfoCardItem label="Barkod" value={product.barcode} icon="barcode" />
                                    )}
                                </InfoCard>
                            </div>

                            {/* Kategori Bilgileri Kartı */}
                            <div className="col-md-6 col-lg-3">
                                <InfoCard icon="tag" title="Kategori Bilgileri" iconColor="primary">
                                    {product.category && (
                                        <InfoCardItem label="Ana Kategori" value={product.category} icon="folder" />
                                    )}
                                    {product.subCategory && (
                                        <InfoCardItem label="Alt Kategori" value={product.subCategory} icon="folder-open" />
                                    )}
                                    
                                    {/* Etiketler */}
                                    {product.tags && product.tags.length > 0 && (
                                        <div className="mt-16 pt-16">
                                            <p className="text-xs text-gray-600 mb-8">Etiketler:</p>
                                            <div className="d-flex flex-wrap gap-6">
                                                {product.tags.map((tag, index) => (
                                                    <InfoCardBadge key={index} text={tag} variant="primary" />
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </InfoCard>
                            </div>

                            {/* Satış Bilgileri Kartı */}
                            <div className="col-md-6 col-lg-3">
                                <InfoCard icon="shopping-bag" title="Satış Bilgileri" iconColor="success">
                                    {product.saleType === 1 && product.minimumOrderWeight && (
                                        <InfoCardItem 
                                            label="Min. Sipariş" 
                                            value={`${product.minimumOrderWeight} gr`} 
                                            icon="shopping-cart" 
                                        />
                                    )}
                                    
                                    <div className={product.saleType === 1 && product.minimumOrderWeight ? "mt-16 pt-16" : ""}>
                                        {product.freeShipping && (
                                            <InfoCardBadge text="Ücretsiz Kargo" variant="success" icon="truck" />
                                        )}
                                        {product.isAvailableForCustomPackage && (
                                            <InfoCardBadge text="Özel Paket Uygun" variant="info" icon="gift" />
                                        )}
                                    </div>
                                </InfoCard>
                            </div>
                        </div>
                    </div>
                    
                    {/* Ürün Detay Sekmeler */}
                    <div className="row">
                        <div className="col-12">
                            <div className="product-details-tabs-modern">
                                <nav>
                                    <div className="nav nav-tabs border-0 mb-16" id="nav-tab" role="tablist">
                                        <button 
                                            className={`nav-link-modern ${activeTab === 'description' ? 'active' : ''}`}
                                            id="description-tab" 
                                            type="button" 
                                            role="tab"
                                            aria-controls="description"
                                            aria-selected={activeTab === 'description'}
                                            onClick={() => handleTabChange('description')}
                                        >
                                            <i className="ph ph-article me-8"></i>
                                            Açıklama
                                        </button>
                                        {product.ingredients && (
                                            <button 
                                                className={`nav-link-modern ${activeTab === 'ingredients' ? 'active' : ''}`}
                                                id="ingredients-tab" 
                                                type="button" 
                                                role="tab"
                                                aria-controls="ingredients"
                                                aria-selected={activeTab === 'ingredients'}
                                                onClick={() => handleTabChange('ingredients')}
                                            >
                                                <i className="ph ph-list-checks me-8"></i>
                                                İçindekiler
                                            </button>
                                        )}
                                        {product.nutritionalInfo && (
                                            <button 
                                                className={`nav-link-modern ${activeTab === 'nutrition' ? 'active' : ''}`}
                                                id="nutrition-tab" 
                                                type="button" 
                                                role="tab"
                                                aria-controls="nutrition"
                                                aria-selected={activeTab === 'nutrition'}
                                                onClick={() => handleTabChange('nutrition')}
                                            >
                                                <i className="ph ph-clipboard-text me-8"></i>
                                                Besin Değerleri
                                            </button>
                                        )}
                                        <button 
                                            className={`nav-link-modern ${activeTab === 'additional' ? 'active' : ''}`}
                                            id="additional-tab" 
                                            type="button" 
                                            role="tab"
                                            aria-controls="additional"
                                            aria-selected={activeTab === 'additional'}
                                            onClick={() => handleTabChange('additional')}
                                        >
                                            <i className="ph ph-info me-8"></i>
                                            Ek Bilgiler
                                        </button>
                                    </div>
                                </nav>
                                <div className="tab-content" id="nav-tabContent">
                                    {/* Açıklama Sekmesi */}
                                    {activeTab === 'description' && (
                                        <div className="tab-pane fade show active" id="description" role="tabpanel" aria-labelledby="description-tab">
                                            <div className="tab-content-card bg-white rounded-20 shadow-sm p-32">
                                                <h5 className="mb-20 fw-bold text-gray-900">
                                                    <i className="ph ph-article text-main-600 me-12"></i>
                                                    Ürün Açıklaması
                                                </h5>
                                                <div 
                                                    className="text-gray-700" 
                                                    style={{ lineHeight: '1.8' }}
                                                    dangerouslySetInnerHTML={{ __html: product.description }}
                                                />
                                            </div>
                                        </div>
                                    )}
                                    
                                    {/* İçindekiler Sekmesi */}
                                    {activeTab === 'ingredients' && product.ingredients && (
                                        <div className="tab-pane fade show active" id="ingredients" role="tabpanel" aria-labelledby="ingredients-tab">
                                            <div className="tab-content-card bg-white rounded-20 shadow-sm p-32">
                                                <h5 className="mb-20 fw-bold text-gray-900">
                                                    <i className="ph ph-list-checks text-main-600 me-12"></i>
                                                    İçindekiler
                                                </h5>
                                                <div className="bg-light p-20 rounded-12">
                                                    <div 
                                                        className="text-gray-700 mb-0" 
                                                        style={{ lineHeight: '1.8' }}
                                                        dangerouslySetInnerHTML={{ __html: product.ingredients }}
                                                    />
                                                </div>
                                                {product.origin && (
                                                    <div className="mt-20 p-16 bg-info bg-opacity-10 rounded-12">
                                                        <small className="text-info d-flex align-items-center gap-8">
                                                            <i className="ph ph-map-pin"></i>
                                                            <strong>Menşei:</strong> {product.origin}
                                                        </small>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    
                                    {/* Besin Değerleri Sekmesi */}
                                    {activeTab === 'nutrition' && product.nutritionalInfo && (
                                        <div className="tab-pane fade show active" id="nutrition" role="tabpanel" aria-labelledby="nutrition-tab">
                                            <div className="tab-content-card bg-white rounded-20 shadow-sm p-32">
                                                <h5 className="mb-20 fw-bold text-gray-900">
                                                    <i className="ph ph-clipboard-text text-main-600 me-12"></i>
                                                    Besin Değerleri
                                                </h5>
                                                <div className="bg-light p-20 rounded-12">
                                                    <div 
                                                        className="text-gray-700 mb-0" 
                                                        style={{ lineHeight: '1.8' }}
                                                        dangerouslySetInnerHTML={{ __html: product.nutritionalInfo }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    
                                    {/* Ek Bilgiler Sekmesi */}
                                    {activeTab === 'additional' && (
                                        <div className="tab-pane fade show active" id="additional" role="tabpanel" aria-labelledby="additional-tab">
                                            <div className="tab-content-card bg-white rounded-20 shadow-sm p-32">
                                                <h5 className="mb-24 fw-bold text-gray-900">
                                                    <i className="ph ph-info text-main-600 me-12"></i>
                                                    Ek Bilgiler
                                                </h5>
                                                <div className="row g-4">
                                                    <div className="col-md-6">
                                                        <div className="info-list">
                                                            {product.barcode && (
                                                                <div className="info-list-item d-flex justify-content-between py-12 border-bottom border-gray-100">
                                                                    <span className="text-gray-600">Barkod:</span>
                                                                    <span className="fw-medium text-gray-900">{product.barcode}</span>
                                                                </div>
                                                            )}
                                                            {product.sku && (
                                                                <div className="info-list-item d-flex justify-content-between py-12 border-bottom border-gray-100">
                                                                    <span className="text-gray-600">SKU:</span>
                                                                    <span className="fw-medium text-gray-900">{product.sku}</span>
                                                                </div>
                                                            )}
                                                            <div className="info-list-item d-flex justify-content-between py-12 border-bottom border-gray-100">
                                                                <span className="text-gray-600">Stok Durumu:</span>
                                                                <span className={`badge ${product.stockQuantity > 0 ? 'bg-success' : 'bg-danger'}`}>
                                                                    {product.stockQuantity > 0 ? 'Stokta' : 'Stok Yok'}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <div className="info-list">
                                                            {product.packageType && (
                                                                <div className="info-list-item d-flex justify-content-between py-12 border-bottom border-gray-100">
                                                                    <span className="text-gray-600">Paket Tipi:</span>
                                                                    <span className="fw-medium text-gray-900">{product.packageType}</span>
                                                                </div>
                                                            )}
                                                            {product.shelfLife && (
                                                                <div className="info-list-item d-flex justify-content-between py-12 border-bottom border-gray-100">
                                                                    <span className="text-gray-600">Raf Ömrü:</span>
                                                                    <span className="fw-medium text-gray-900">{product.shelfLife}</span>
                                                                </div>
                                                            )}
                                                            {product.storageConditions && (
                                                                <div className="info-list-item d-flex justify-content-between py-12 border-bottom border-gray-100">
                                                                    <span className="text-gray-600">Saklama:</span>
                                                                    <span className="fw-medium text-gray-900">{product.storageConditions}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                                
                                                {/* Özellikler Badges */}
                                                <div className="mt-32 pt-32">
                                                    <h6 className="mb-16 fw-semibold text-gray-900">Ürün Özellikleri</h6>
                                                    <div className="d-flex flex-wrap gap-12">
                                                        {product.isNew && (
                                                            <div className="feature-badge d-flex align-items-center gap-8 px-16 py-12 bg-primary bg-opacity-10 rounded-12">
                                                                <i className="ph ph-sparkle text-primary text-20"></i>
                                                                <span className="text-primary fw-medium">Yeni Ürün</span>
                                                            </div>
                                                        )}
                                                        {product.isFeatured && (
                                                            <div className="feature-badge d-flex align-items-center gap-8 px-16 py-12 bg-warning bg-opacity-10 rounded-12">
                                                                <i className="ph ph-star text-warning text-20"></i>
                                                                <span className="text-warning fw-medium">Öne Çıkan</span>
                                                            </div>
                                                        )}
                                                        {product.freeShipping && (
                                                            <div className="feature-badge d-flex align-items-center gap-8 px-16 py-12 bg-success bg-opacity-10 rounded-12">
                                                                <i className="ph ph-truck text-success text-20"></i>
                                                                <span className="text-success fw-medium">Ücretsiz Kargo</span>
                                                            </div>
                                                        )}
                                                        {product.isAvailableForCustomPackage && (
                                                            <div className="feature-badge d-flex align-items-center gap-8 px-16 py-12 bg-info bg-opacity-10 rounded-12">
                                                                <i className="ph ph-package text-info text-20"></i>
                                                                <span className="text-info fw-medium">Özel Paket</span>
                                                            </div>
                                                        )}
                                                        {product.isDiscounted && (
                                                            <div className="feature-badge d-flex align-items-center gap-8 px-16 py-12 bg-danger bg-opacity-10 rounded-12">
                                                                <i className="ph ph-percent text-danger text-20"></i>
                                                                <span className="text-danger fw-medium">İndirimli</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Mobile Bottom Navigation */}
            {product && (
                <>
                    <div className="mobile-bottom-nav">
                        {/* Sol taraf - Fiyat bilgisi */}
                        <div className="mobile-bottom-nav-price">
                            <div className="mobile-bottom-nav-price-amount">
                                ₺{(product.discountedPrice || product.price).toFixed(2)}
                            </div>
                            <div className="mobile-bottom-nav-price-unit">
                                / {product.saleType === 1 ? 'Kg' : 'Adet'}
                            </div>
                            <div className="mobile-bottom-nav-quantity">
                                {quantity} {unit === 'kg' ? 'Kg' : unit === 'gram' ? 'gr' : 'Adet'} seçili
                            </div>
                        </div>

                        {/* Sağ taraf - Action buttons */}
                        <div className="mobile-bottom-nav-actions">
                            <button
                                type="button"
                                className="mobile-bottom-nav-btn mobile-bottom-nav-btn-primary"
                                onClick={handleAddToCart}
                                disabled={product.isOutOfStock}
                            >
                                <i className="ph ph-shopping-cart"></i>
                                {product.isOutOfStock ? 'Stok Yok' : 'Sepete Ekle'}
                            </button>
                            
                            <a
                                href={whatsappUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mobile-bottom-nav-btn mobile-bottom-nav-btn-whatsapp"
                                title="WhatsApp ile Sipariş Ver"
                            >
                                <i className="ph ph-whatsapp-logo"></i>
                            </a>
                        </div>
                    </div>

                    {/* Mobile Quantity Selector Bottom Sheet */}
                    <div 
                        className={`mobile-quantity-overlay ${showQuantitySheet ? 'active' : ''}`}
                        onClick={() => setShowQuantitySheet(false)}
                    />
                    <div className={`mobile-quantity-sheet ${showQuantitySheet ? 'active' : ''}`}>
                        <div className="mobile-quantity-sheet-header">
                            <div className="mobile-quantity-sheet-title">Miktar Seçin</div>
                            <button 
                                className="mobile-quantity-sheet-close"
                                onClick={() => setShowQuantitySheet(false)}
                            >
                                <i className="ph ph-x"></i>
                            </button>
                        </div>
                        <div className="mobile-quantity-sheet-content">
                            <UnitSelector
                                saleType={product.saleType || 0}
                                minimumOrderWeight={product.minimumOrderWeight || 250}
                                price={product.discountedPrice || product.price}
                                discountedPrice={product.discountedPrice}
                                onQuantityChange={handleQuantityChange}
                                initialQuantity={product.saleType === 1 ? 1 : 1}
                                initialUnit={product.saleType === 1 ? 'kg' : 'piece'}
                                showPrice={true}
                                size="md"
                            />
                        </div>
                        <div className="mobile-quantity-sheet-footer">
                            <button 
                                className="mobile-quantity-sheet-confirm"
                                onClick={() => setShowQuantitySheet(false)}
                            >
                                Tamam
                            </button>
                        </div>
                    </div>
                </>
            )}
        </>
    )
}

export default ProductDetailsOne
